import { useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { ExpandedAgentIdleState } from '@features/Admin/Agent/model/idleState';
import { SETTINGS_CATEGORY_FORM_FIELDS, SETTINGS_CATEGORY_KEYS } from './constants';
import { SettingsCategoryKey } from './types';

export const useAdvancedSettingsCategoryNavigation = ({
  open,
  agentId,
  isDirty,
  formMethods,
  validateIdleState,
}: {
  open: boolean;
  agentId?: string;
  isDirty: boolean;
  formMethods: UseFormReturn<AgentToolsFormValue>;
  validateIdleState: () => ExpandedAgentIdleState | null;
}) => {
  const { trigger, getFieldState } = formMethods;
  const [activeCategory, setActiveCategory] = useState<SettingsCategoryKey>('callLifecycle');
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveCategory('callLifecycle');
    }
  }, [open, agentId]);

  const getFirstValidationErrorCategory = useCallback(
    (isIdleStateValid: boolean): SettingsCategoryKey | null =>
      SETTINGS_CATEGORY_KEYS.find((category) => {
        if (category === 'idleAndCustomer') {
          return !isIdleStateValid;
        }

        return SETTINGS_CATEGORY_FORM_FIELDS[category].some((fieldName) =>
          Boolean(getFieldState(fieldName).error),
        );
      }) ?? null,
    [getFieldState],
  );

  const handleCategoryChange = useCallback(
    async (_event: React.SyntheticEvent, value: SettingsCategoryKey) => {
      if (!isDirty) {
        setActiveCategory(value);
        return;
      }

      const isFormValid = await trigger();
      const isIdleStateValid = validateIdleState() !== null;

      if (!isFormValid || !isIdleStateValid) {
        setActiveCategory(getFirstValidationErrorCategory(isIdleStateValid) ?? value);
        setShowValidationDialog(true);
        return;
      }

      setActiveCategory(value);
    },
    [getFirstValidationErrorCategory, isDirty, trigger, validateIdleState],
  );

  return {
    activeCategory,
    showValidationDialog,
    handleCategoryChange,
    closeValidationDialog: () => setShowValidationDialog(false),
  };
};
